import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { Application } from '@/types/database.types';
import { useCreateApplication } from './application/use-create-application';
import { useFetchApplication } from './application/use-fetch-application';
import { useAuthStatus } from './application/use-auth-status';

// Improved type check for Application
const isApplication = (data: any): data is Application => {
  return data && typeof data.id === 'string';
};

export const useApplicationData = (userIdFromUrl?: string | null) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [application, setApplication] = useState<Application | null>(null);

  // Use our hooks
  const { createNewApplication, isCreating } = useCreateApplication();
  const { fetchApplication, isLoading: isFetching } = useFetchApplication();
  const { checkAuthStatus, isCheckingAuth } = useAuthStatus();

  // Extract the application ID from the URL for selective loading
  const getApplicationIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('id');
  };

  useEffect(() => {
    let isMounted = true;
    
    const loadOrCreateApplication = async () => {
      if (!isMounted) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        console.log('Starting to load application data', { userIdFromUrl });
        
        // Step 1: Check authentication status
        const authUser = await checkAuthStatus();
        
        // Check if there's a specific application ID in the URL
        const applicationId = getApplicationIdFromUrl();
        
        // Step 2: Try to fetch an existing application
        let existingApplication = null;
        
        try {
          if (applicationId) {
            // If application ID is specified, fetch that specific application
            // If userIdFromUrl is provided (broker viewing client app), use that instead of current user's ID
            const userIdToUse = userIdFromUrl || authUser.id;
            console.log(`Fetching application ${applicationId} for user ${userIdToUse}`);
            existingApplication = await fetchApplication(userIdToUse, applicationId);
          } else if (!userIdFromUrl) {
            // Only fetch most recent if not viewing as broker (userIdFromUrl not provided)
            // Otherwise, fetch the most recent application for current user
            existingApplication = await fetchApplication(authUser.id);
          }
        } catch (e) {
          console.error("Error checking for existing applications:", e);
        }
        
        if (existingApplication && isApplication(existingApplication)) {
          console.log("Using existing application:", existingApplication.id);
          if (isMounted) {
            setApplication(existingApplication);
            setIsLoading(false);
          }
          return;
        }
        
        // Step 3: Create a new application if none exists and not viewing as broker
        if (!userIdFromUrl) {
          console.log('Creating new application for user:', authUser.id);
          
          const newApplication = await createNewApplication(authUser.id);
          
          if (newApplication && isMounted) {
            setApplication(newApplication);
          }
        } else {
          // If we're a broker viewing a client's application but couldn't find it
          console.error("Could not find application for linked user:", userIdFromUrl);
          if (isMounted) {
            setError(new Error("Could not find application for this user"));
            toast({
              title: 'Application not found',
              description: "Could not find the requested application for this user",
              variant: 'destructive',
            });
          }
        }
      } catch (error: any) {
        console.error('Application loading error:', error);
        if (isMounted) {
          setError(error);
          
          toast({
            title: 'Error loading application',
            description: error.message || "Could not load or create application",
            variant: 'destructive',
          });
          
          navigate('/');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrCreateApplication();
    
    return () => {
      isMounted = false;
    };
  }, [toast, navigate, userIdFromUrl]);

  // Combine loading states from all hooks
  const combinedLoading = isLoading || isCreating || isFetching || isCheckingAuth;

  return {
    application,
    setApplication,
    isLoading: combinedLoading,
    error,
  };
};
